import { useCallback, useEffect, useId, useMemo, useState, type FC } from "react";
import { CalendarIcon, CopyIcon, CreditCardIcon, CrownIcon, GiftIcon, RefreshCwIcon, RefreshCwOffIcon, TriangleAlertIcon } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/ds/shadcn/components/ui/alert";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Field, FieldLabel } from "@/ds/shadcn/components/ui/field";
import { Input } from "@/ds/shadcn/components/ui/input";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { API_BASE_URL, DOCS_BASE_URL, FREE_TRIAL_PROMO_CODE } from "@/lib/common/config";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { Limit } from "@/lib/common/Limit";
import { LimitHeader } from "@/lib/common/LimitHeader";
import { LimitHeaderSubtitle } from "@/lib/common/LimitHeaderSubtitle";
import { LimitHeaderTitle } from "@/lib/common/LimitHeaderTitle";
import { useTitle } from "@/lib/common/useTitle";
import { getPaymentCardProviderImage } from "@/lib/media/paymentCardProviders";
import { useSubscription, type ActiveSubscriptionModel, type SubscriptionModel } from "@/features/common/api/useSubscription";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { usePaymentCards } from "@/features/paymentCards/api/usePaymentCards";
import { useClientCredentials } from "@/features/subscriptions/api/useClientCredentials";
import { IconTile } from "@/features/subscriptions/components/IconTile";
import { ModalCancelSubscription } from "@/features/subscriptions/components/ModalCancelSubscription";
import { ModalChangePaymentCard } from "@/features/subscriptions/components/ModalChangePaymentCard";
import { ModalSubscribe, type SubscribeOptions } from "@/features/subscriptions/components/ModalSubscribe";
import { SubscriptionPlanCard, type SubscriptionPlanFeature } from "@/features/subscriptions/components/SubscriptionPlanCard";
import {
	canCancelSubscription,
	formatSubscriptionDate,
	getCurrentPlanId,
	getCurrentPlanPreference,
	getSubscribeMode,
	hasAutoRenewPaymentFailure,
	isAutoRenewEnabled,
	isFreeTrialAvailable,
	type PlanPreference
} from "@/features/subscriptions/SubscriptionLogic";

const FREE_PLAN_FEATURES: SubscriptionPlanFeature[] = [
	{ text: "Organize your products into collections" },
	{ text: "Track the market value of your collections and products" },
	{ text: "Import up to 5000 rows of CSV" },
	{ text: "Set your preferred currency for pricing information" },
	{ text: "Buy your favourite products through TCGplayer" },
	{ text: "Regularly updated pricing information from TCGplayer" }
];

const PREMIUM_PLAN_FEATURES: SubscriptionPlanFeature[] = [
	{ text: "Includes all free features", inherited: true },
	{ text: "View the Estimated Value (EV) for sealed product" },
	{ text: "View latest market trends by product" },
	{ text: "View your total invested value" },
	{ text: "View potential profits made on your products" },
	{ text: "Track profit and loss through sales" },
	{ text: "View the cash position for any of your collections" },
	{ text: "Historical graphs for all your collection data" },
	{ text: "Export your collections in CSV format" },
	{ text: "Unlimited CSV imports" },
	{ text: "Direct our future by participating in feature polls" },
	{ text: "Share your collections publicly" }
];

const BUSINESS_PLAN_FEATURES: SubscriptionPlanFeature[] = [
	{ text: "Includes all premium features", inherited: true },
	{ text: "Access to our Developer API" }
];

export const PageSubscription: FC = () => {
	useTitle("Subscription");

	const breadcrumb = useMemo(() => [
		{ label: "Settings" },
		{ label: "Subscription" }
	], []);

	const subscription = useSubscription();

	const [wasSubscribedOnMount, setWasSubscribedOnMount] = useState<boolean | null>(null);
	const [subscribeModalOpen, setSubscribeModalOpen] = useState(false);
	const [subscribeOptions, setSubscribeOptions] = useState<SubscribeOptions>({ mode: "Subscribe", planPreference: "Standard", preselectedPlanId: null });
	const [changePaymentCardModalOpen, setChangePaymentCardModalOpen] = useState(false);
	const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] = useState(false);

	useEffect(() => {
		if (!subscription.data || wasSubscribedOnMount !== null) {
			return;
		}

		setWasSubscribedOnMount(subscription.data.activeSubscription !== null);
	}, [subscription.data]);

	const openSubscribeModal = useCallback((planPreference: PlanPreference, preselectedPlanId: string | null, promoCode?: string) => {
		if (!subscription.data) {
			return;
		}

		setSubscribeOptions({
			mode: getSubscribeMode(subscription.data),
			planPreference,
			preselectedPlanId,
			promoCode
		});

		setSubscribeModalOpen(true);
	}, [subscription.data]);

	const onSubscribeToPlan = useCallback((planPreference: PlanPreference, promoCode?: string) => {
		openSubscribeModal(planPreference, null, promoCode);
	}, [openSubscribeModal]);

	const onChangePlan = useCallback(() => {
		if (!subscription.data) {
			return;
		}

		openSubscribeModal(getCurrentPlanPreference(subscription.data), getCurrentPlanId(subscription.data));
	}, [subscription.data, openSubscribeModal]);

	const onChangePaymentCard = useCallback(() => setChangePaymentCardModalOpen(true), []);
	const onCancelSubscription = useCallback(() => setCancelSubscriptionModalOpen(true), []);

	return (
		<>
			<HeaderBar
				title="Subscription"
				description="Manage your access to premium features"
				breadcrumb={breadcrumb} />

			{
				subscription.isLoading && (
					<div className="flex justify-center p-3">
						<Spinner />
					</div>
				)
			}

			{
				subscription.error && (
					<div className="p-3">
						<ErrorDisplay error={subscription.error} />
					</div>
				)
			}

			{
				subscription.data && (
					<>
						{
							subscription.data.activeSubscription ? (
								<ActiveSubscriptionView
									subscription={subscription.data}
									activeSubscription={subscription.data.activeSubscription}
									canCancel={canCancelSubscription(subscription.data, wasSubscribedOnMount)}
									onChangePlan={onChangePlan}
									onChangePaymentCard={onChangePaymentCard}
									onCancel={onCancelSubscription} />
							) : (
								<NoSubscriptionView
									subscription={subscription.data}
									onSubscribe={onSubscribeToPlan}
									onChangePaymentCard={onChangePaymentCard} />
							)
						}

						<ModalSubscribe
							open={subscribeModalOpen}
							onOpenChange={setSubscribeModalOpen}
							subscription={subscription.data}
							options={subscribeOptions} />

						<ModalChangePaymentCard
							open={changePaymentCardModalOpen}
							onOpenChange={setChangePaymentCardModalOpen}
							subscription={subscription.data} />

						<ModalCancelSubscription
							open={cancelSubscriptionModalOpen}
							onOpenChange={setCancelSubscriptionModalOpen}
							subscription={subscription.data} />
					</>
				)
			}
		</>
	);
};

interface ActiveSubscriptionViewProps {
	subscription: SubscriptionModel;
	activeSubscription: ActiveSubscriptionModel;
	canCancel: boolean;
	onChangePlan: () => void;
	onChangePaymentCard: () => void;
	onCancel: () => void;
}

const ActiveSubscriptionView: FC<ActiveSubscriptionViewProps> = props => {
	const {
		subscription,
		activeSubscription,
		canCancel,
		onChangePlan,
		onChangePaymentCard,
		onCancel
	} = props;

	const autoRenewEnabled = isAutoRenewEnabled(subscription);

	return (
		<>
			{
				hasAutoRenewPaymentFailure(subscription) && (
					<Limit>
						<div className="mt-3">
							<PaymentFailedAlert onChangePaymentCard={onChangePaymentCard} />
						</div>
					</Limit>
				)
			}

			<Limit border={true}>
				<LimitHeader>
					<LimitHeaderTitle>Subscription Details</LimitHeaderTitle>
				</LimitHeader>

				<div className="flex flex-col divide-y">
					<SubscriptionDetailRow
						media={<IconTile><CrownIcon /></IconTile>}
						title="Current Plan"
						subtitle={activeSubscription.planName} />

					<SubscriptionDetailRow
						media={<IconTile><CalendarIcon /></IconTile>}
						title={autoRenewEnabled ? "Renews" : "Ends"}
						subtitle={formatSubscriptionDate(activeSubscription.expiryDate)} />

					{
						autoRenewEnabled ? (
							<>
								<SubscriptionDetailRow
									media={<IconTile><RefreshCwIcon /></IconTile>}
									title="Auto Renew Plan"
									subtitle={subscription.autoRenewPlanName}
									trailing={<MoneyDisplay className="text-sm font-medium" mode="Standard" money={{ value: subscription.autoRenewPlanPrice, currency: "USD" }} />} />

								<AutoRenewPaymentCardRow
									paymentCardId={subscription.autoRenewPaymentCardId}
									onChangePaymentCard={onChangePaymentCard} />
							</>
						) : (
							<SubscriptionDetailRow
								media={<IconTile><RefreshCwOffIcon /></IconTile>}
								title="Auto Renew"
								subtitle="Off" />
						)
					}
				</div>

				<div className="mt-6 flex flex-col-reverse gap-2 @md:flex-row @md:justify-end">
					{
						canCancel && (
							<Button variant="outline" onClick={onCancel}>
								Cancel Subscription
							</Button>
						)
					}
					<Button onClick={onChangePlan}>
						{getSubscribeMode(subscription) === "Resume" ? "Resume Subscription" : "Change Plan"}
					</Button>
				</div>
			</Limit>

			{activeSubscription.clientCredentialsAccess && <ClientCredentialsSection />}
		</>
	);
};

interface AutoRenewPaymentCardRowProps {
	paymentCardId: string | null;
	onChangePaymentCard: () => void;
}

const AutoRenewPaymentCardRow: FC<AutoRenewPaymentCardRowProps> = props => {
	const {
		paymentCardId,
		onChangePaymentCard
	} = props;

	const paymentCards = usePaymentCards();
	const paymentCard = useMemo(() => paymentCards.data?.find(x => x.paymentCardId === paymentCardId) ?? null, [paymentCards.data, paymentCardId]);

	const subtitle = useMemo(() => {
		if (paymentCard) {
			return `Card ending in ${paymentCard.last4}`;
		}

		return paymentCards.isLoading ? "Loading..." : "No card on file";
	}, [paymentCard, paymentCards.isLoading]);

	return (
		<SubscriptionDetailRow
			media={
				paymentCard
					? <img src={getPaymentCardProviderImage(paymentCard.provider)} className="size-8 shrink-0" />
					: <IconTile><CreditCardIcon /></IconTile>
			}
			title="Auto Renew Card"
			subtitle={subtitle}
			trailing={<Button variant="outline" size="sm" onClick={onChangePaymentCard}>Change</Button>} />
	);
};

interface SubscriptionDetailRowProps {
	media: React.ReactNode;
	title: string;
	subtitle: React.ReactNode;
	trailing?: React.ReactNode;
}

const SubscriptionDetailRow: FC<SubscriptionDetailRowProps> = props => {
	const {
		media,
		title,
		subtitle,
		trailing
	} = props;

	return (
		<div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
			{media}
			<div className="min-w-0 flex-1">
				<div className="truncate text-sm font-medium">{title}</div>
				<div className="truncate text-xs text-muted-foreground">{subtitle}</div>
			</div>
			{trailing}
		</div>
	);
};

interface NoSubscriptionViewProps {
	subscription: SubscriptionModel;
	onSubscribe: (planPreference: PlanPreference, promoCode?: string) => void;
	onChangePaymentCard: () => void;
}

const NoSubscriptionView: FC<NoSubscriptionViewProps> = props => {
	const {
		subscription,
		onSubscribe,
		onChangePaymentCard
	} = props;

	return (
		<div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-3 @md:p-6">
			{hasAutoRenewPaymentFailure(subscription) && <PaymentFailedAlert onChangePaymentCard={onChangePaymentCard} />}

			{
				FREE_TRIAL_PROMO_CODE && isFreeTrialAvailable(subscription, FREE_TRIAL_PROMO_CODE) && (
					<FreeTrialAlert
						promoCode={FREE_TRIAL_PROMO_CODE}
						onStartFreeTrial={() => onSubscribe("Standard", FREE_TRIAL_PROMO_CODE)} />
				)
			}

			<div className="grid gap-3 @3xl:grid-cols-3">
				<SubscriptionPlanCard
					title="Free"
					description="We will always offer these features to everyone for free!"
					amount={0}
					features={FREE_PLAN_FEATURES} />

				<SubscriptionPlanCard
					title="Premium"
					description="Upgrade your experience with these premium features!"
					amount={4.99}
					discounted
					features={PREMIUM_PLAN_FEATURES}
					onSubscribe={() => onSubscribe("Standard")} />

				<SubscriptionPlanCard
					title="Business"
					description="Integrate your store or business with Fyendal's Collection directly"
					amount={19.99}
					discounted
					features={BUSINESS_PLAN_FEATURES}
					onSubscribe={() => onSubscribe("ClientCredentials")} />
			</div>
		</div>
	);
};

interface PaymentFailedAlertProps {
	onChangePaymentCard: () => void;
}

const PaymentFailedAlert: FC<PaymentFailedAlertProps> = props => {
	const {
		onChangePaymentCard
	} = props;

	return (
		<Alert variant="destructive">
			<TriangleAlertIcon />
			<AlertTitle>Payment Failed</AlertTitle>
			<AlertDescription>
				<p>Your payment card was declined when renewing your subscription. Please update your payment card to keep your premium access.</p>
				<Button className="mt-2" variant="outline" size="sm" onClick={onChangePaymentCard}>
					Update Payment Card
				</Button>
			</AlertDescription>
		</Alert>
	);
};

interface FreeTrialAlertProps {
	promoCode: string;
	onStartFreeTrial: () => void;
}

const FreeTrialAlert: FC<FreeTrialAlertProps> = props => {
	const {
		promoCode,
		onStartFreeTrial
	} = props;

	return (
		<Alert>
			<GiftIcon />
			<AlertTitle>Free Trial</AlertTitle>
			<AlertDescription>
				<p>Get all our premium features for free for 7 days with promo code <span className="font-medium text-foreground">{promoCode}</span>.</p>
				<Button className="mt-2" size="sm" onClick={onStartFreeTrial}>
					Start Free Trial
				</Button>
			</AlertDescription>
		</Alert>
	);
};

const ClientCredentialsSection: FC = () => {
	const clientCredentials = useClientCredentials();

	return (
		<Limit border={true}>
			<LimitHeader>
				<LimitHeaderTitle>API Credentials</LimitHeaderTitle>
				<LimitHeaderSubtitle>
					Your subscription includes access to our API. You can use the credentials below
					to authenticate your requests. For more information, consult
					our <a className="text-primary underline-offset-4 hover:underline" href={DOCS_BASE_URL} target="_blank" rel="noreferrer">API Documentation</a>.
				</LimitHeaderSubtitle>
			</LimitHeader>

			<div className="flex flex-col gap-4">
				<CredentialField label="Token URL" value={`${API_BASE_URL}/oauth2/token`} />

				{
					clientCredentials.isLoading && (
						<div className="flex justify-center p-3">
							<Spinner />
						</div>
					)
				}

				<ErrorDisplay error={clientCredentials.error} />

				{
					clientCredentials.data && (
						<>
							<CredentialField label="Client ID" value={clientCredentials.data.clientId} />
							<CredentialField label="Client Secret" value={clientCredentials.data.clientSecret} secret />
						</>
					)
				}
			</div>
		</Limit>
	);
};

interface CredentialFieldProps {
	label: string;
	value: string;
	secret?: boolean;
}

const CredentialField: FC<CredentialFieldProps> = props => {
	const {
		label,
		value,
		secret = false
	} = props;

	const id = useId();

	const onCopy = useCallback(() => {
		navigator.clipboard.writeText(value)
			.then(() => toast.success(`${label} copied to clipboard`, { position: "bottom-center" }))
			.catch(() => toast.error(`Couldn't copy ${label}`, { position: "bottom-center" }));
	}, [label, value]);

	return (
		<Field>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<div className="flex items-center gap-2">
				<Input
					id={id}
					className="font-mono text-xs"
					type={secret ? "password" : "text"}
					value={value}
					readOnly
					onFocus={e => e.target.select()} />
				<Button variant="outline" size="icon" aria-label={`Copy ${label}`} onClick={onCopy}>
					<CopyIcon />
				</Button>
			</div>
		</Field>
	);
};
